import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, BadgeCheck, Loader2 } from "lucide-react";
import SEO from "../components/SEO";
import TherapyBookingButton from "../components/TherapyBookingButton";
import { API } from "../lib/api";

export default function PractitionerPage() {
  const { id } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: false });

  useEffect(() => {
    fetch(`${API}/site/therapy/practitioners/${id}`)
      .then((r) => { if (!r.ok) throw new Error("nf"); return r.json(); })
      .then((d) => setState({ loading: false, data: d, error: false }))
      .catch(() => setState({ loading: false, data: null, error: true }));
  }, [id]);

  if (state.loading) {
    return (
      <div className="flex items-center gap-2 justify-center py-32 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (state.error || !state.data) {
    return (
      <section className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="practitioner-not-found">
        <h1 className="font-display text-2xl font-extrabold" style={{ color: "#123B6D" }}>Practitioner not found.</h1>
        <Link to="/play-based-support" className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#00A6B2]">
          <ArrowLeft className="h-4 w-4" /> Back to Therapy & Play-Based Support
        </Link>
      </section>
    );
  }

  const { practitioner: p, booking } = state.data;
  return (
    <>
      <SEO
        path={`/play-based-support/practitioners/${p.id}`}
        title={`${p.name} | Therapy & Play-Based Support | Nexora`}
        description={`${p.name}${p.title ? ` — ${p.title}` : ""} at Nexora Learning & Development Centre in Richmond BC.`}
      />
      <section className="bg-[#FFFCF7]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16" data-testid="practitioner-page">
          <Link to="/play-based-support" className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-700">
            <ArrowLeft className="h-3.5 w-3.5" /> Therapy & Play-Based Support
          </Link>
          <div className="mt-6 grid md:grid-cols-[220px_1fr] gap-8 items-start">
            <div className="rounded-[1.75rem] overflow-hidden border border-slate-200 bg-white aspect-[4/5]">
              {p.photo_url ? (
                <img src={`${API.replace(/\/api$/, "")}${p.photo_url}`} alt={p.name} className="h-full w-full object-cover" data-testid="practitioner-photo" />
              ) : (
                <div className="h-full w-full flex items-center justify-center bg-[#EAF7FB] font-display text-4xl font-extrabold" style={{ color: "#123B6D" }}>
                  {p.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                </div>
              )}
            </div>
            <div>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: "#123B6D" }} data-testid="practitioner-name">
                {p.name}
              </h1>
              {(p.title || p.designation) && (
                <p className="mt-1.5 text-sm font-bold text-[#00A6B2]">
                  {[p.title, p.designation].filter(Boolean).join(" · ")}
                </p>
              )}
              {p.qualifications && <p className="mt-2 text-sm text-slate-600">{p.qualifications}</p>}
              {p.registration_number && (
                <p className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500" data-testid="practitioner-registration">
                  <BadgeCheck className="h-3.5 w-3.5 text-[#6DBE45]" /> Registration: {p.registration_number}
                </p>
              )}
              {p.bio && <p className="mt-4 text-sm text-slate-700 leading-relaxed whitespace-pre-line" data-testid="practitioner-bio">{p.bio}</p>}
              {p.areas.length > 0 && (
                <div className="mt-5">
                  <p className="eyebrow" style={{ color: "#00A6B2" }}>Areas of support</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {p.areas.map((a) => (
                      <span key={a} className="pill bg-white border border-slate-200 text-slate-700 text-xs">{a}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-7">
                <TherapyBookingButton booking={booking} practitionerId={p.id} testId="practitioner-book-btn" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
